'use client';

import { QueryClient } from '@tanstack/react-query';

/**
 * Calculate staleTime for monitor queries based on monitor frequency
 * StaleTime should be slightly longer than monitor frequency to avoid
 * unnecessary refetches between monitor checks
 * 
 * @param monitorFrequency - Monitor check frequency in seconds
 * @returns StaleTime in milliseconds
 */
export function getMonitorStaleTime(monitorFrequency: number): number {
  // Convert frequency from seconds to milliseconds
  const frequencyMs = monitorFrequency * 1000;
  
  // Add 50% buffer to avoid refetching too frequently
  // Minimum 30 seconds, maximum 5 minutes
  const staleTime = Math.min(Math.max(frequencyMs * 1.5, 30000), 300000);
  
  return staleTime;
}

/**
 * QueryClient configuration with stale-while-revalidate defaults
 * 
 * Default settings:
 * - staleTime: 2 minutes (data considered fresh)
 * - gcTime: 5 minutes (how long unused data stays in cache)
 * - refetchOnWindowFocus: true (refetch when user returns to tab)
 * - refetchOnReconnect: true (refetch on network reconnect)
 * - retry: 3 attempts with exponential backoff
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
    },
  },
});

